import React from 'react';
import { styled } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import SensorImg from './images/product-diagram.png';

const StyledText = styled(Typography)(({ theme, top, left, width, textAlign }) => ({
  position: 'absolute',
  top: top || 0,
  left: left || 0,
  transform: 'translate(-50%, -50%)',
  fontFamily: 'ZalandoSans',
  fontWeight: '800',
  color: theme.palette.error.main,
  fontSize: '20px',
  lineHeight: 1.2,
  width: width || '300px',
  textAlign: textAlign || 'center',

  [`@media screen and (max-width: ${theme.breakpoints.values.lg - 1}px)`]: {
    fontSize: '18px',
    width: `calc(${width} - 20px)`,
  },

  [`@media screen and (max-width: ${theme.breakpoints.values.md - 1}px)`]: {
    fontSize: '15px',
    width: `calc(${width} - 35px)`,
  },

  [`@media screen and (max-width: ${theme.breakpoints.values.sm - 1}px)`]: {
    fontSize: '15px',
    width: `calc(${width} - 35px)`,
  },

  [`@media screen and (max-width: 600px)`]: {
    fontSize: '12px',
    width: `calc(${width} - 60px)`,
  },

  [`@media screen and (max-width: 550px)`]: {
    fontSize: '11px',
    width: `calc(${width} - 70px)`,
  },
}));

const VerticalLine = styled('hr')(({ theme, top, bottom, left }) => ({
  border: 'none',
  borderRight: `2px dashed ${theme.palette.common.black}`,
  width: '5px',
  top: top || '0',
  bottom: bottom || '0',
  left: left || '50%',
  position: 'absolute',

  [`@media screen and (max-width: 800px)`]: {
    borderRight: `1px dashed ${theme.palette.common.black}`,
  },
}));

const HorizontalLine = styled('hr')(({ theme, top, left, width }) => ({
  border: 'none',
  borderTop: `2px dashed ${theme.palette.common.black}`,
  width: width || '5px',
  top: top || '0',
  left: left || '50%',
  position: 'absolute',

  [`@media screen and (max-width: ${theme.breakpoints.values.md - 1}px)`]: {
    left: `calc(${left} + 10px)`,
    width: `calc(${width} - 2%)`,
  },

  [`@media screen and (max-width: 800px)`]: {
    borderTop: `1px dashed ${theme.palette.common.black}`,
    left: `calc(${left} + 10px)`,
    width: `calc(${width} - 3%)`,
    top: `calc(${top} - 4%)`,
  },

  [`@media screen and (max-width: 700px)`]: {
    left: `calc(${left} + 20px)`,
    width: `calc(${width} - 6%)`,
  },
}));

const verticalItems = [
  {
    label: 'SERIAL NUMBER FOR BLUETOOTH CONNECTION',
    textTop: '-35%',
    textLeft: '58%',
    lineTop: '-28%',
    lineBottom: '95%',
    lineLeft: '58%',
    width: '300px',
  },
  {
    label: 'POWER BUTTON',
    textTop: '130%',
    textLeft: '55.5%',
    lineTop: '86%',
    lineBottom: '-22%',
    lineLeft: '55%',
    width: '150px',
  },
  {
    label: 'TRIPOD MOUNT',
    textTop: '130%',
    textLeft: '38.5%',
    lineTop: '89%',
    lineBottom: '-22%',
    lineLeft: '38%',
    width: '150px',
  },
];

const horizontalItems = [
  {
    label: 'INDICATOR LIGHT',
    textTop: '16%',
    textLeft: '89%',
    lineTop: '13.5%',
    lineWidth: '20%',
    lineLeft: '60.5%',
    width: '175px',
    textAlign: 'left',
  },
  {
    label: 'CHARGING PORT',
    textTop: '72.5%',
    textLeft: '88.75%',
    lineTop: '69.5%',
    lineWidth: '22%',
    lineLeft: '58.5%',
    width: '170px',
    textAlign: 'left',
  },
  {
    label: 'PARTICULATE SENSOR',
    textTop: '18%',
    textLeft: '14.5%',
    lineTop: '15.5%',
    lineWidth: '11%',
    lineLeft: '22%',
    width: '145px',
    textAlign: 'right',
  },
  {
    label: 'VOC, CO2 TEMPERATURE, AND HUMIDITY SENSOR',
    textTop: '50%',
    textLeft: '12%',
    lineTop: '47.5%',
    lineWidth: '10%',
    lineLeft: '22%',
    width: '208px',
    textAlign: 'right',
  },
];

export default function SensorSection() {
  return (
    <Box id="sensor" sx={{ p: '0!important' }}>
      <Box sx={{ maxWidth: '1200px', margin: '0 auto', pt: 6, pb: 3, px: { lg: '0', xs: '32px' } }}>
        <Typography
          sx={{
            fontFamily: 'ZalandoSans',
            fontWeight: '800',
            color: 'error.main',
            fontSize: '30px',
            lineHeight: 1.3,
          }}
        >
          THE SENSOR
        </Typography>
        <Typography
          sx={{
            mt: 2,
            mb: 3,
            fontFamily: 'ZalandoSans',
            fontWeight: '800',
            fontSize: '70px',
            width: '35vw',
            maxWidth: '375px',
            lineHeight: 1,
          }}
        >
          Air Quality Sensor 2.0
        </Typography>
        <Typography
          sx={{
            fontFamily: 'ZalandoSans',
            fontWeight: '800',
            color: 'error.main',
            fontSize: '30px',
            lineHeight: 1.3,
            mb: 1,
          }}
        >
          Real-Time Air Quality Insights
        </Typography>
        <Typography
          sx={{
            fontFamily: 'ZalandoSans',
            fontWeight: '400',
            fontSize: '25px',
            lineHeight: 1,
            mb: 4,
          }}
        >
          SMALL, PORTABLE, POWERFUL
        </Typography>
        <Typography
          sx={{
            fontFamily: 'ZalandoSans',
            fontWeight: '600',
            fontSize: '22px',
            lineHeight: 1.5,
            mb: 25,
          }}
        >
          Our latest model of the Air Quality Sensor incorporates particulate matter, carbon dioxide, temperature,
          humidity, and volatile organic compound measurements into one device that’s about the size of your smartphone!
          Paired with our mobile application, video and sensor data is collected simultaneously for pinpointing airborne
          hazard sources and streamlining assessments.
        </Typography>
        <Box sx={{ width: '100%', my: 25, position: 'relative', display: 'flex', justifyContent: 'center' }}>
          <img src={SensorImg} alt="VPSi Sensor Diagram" style={{ width: '50vw', maxWidth: '600px' }} />
          {verticalItems.map((item, index) => (
            <React.Fragment key={`vertical-item-${index}`}>
              <StyledText top={item.textTop} left={item.textLeft} width={item.width}>
                {item.label}
              </StyledText>
              <VerticalLine top={item.lineTop} bottom={item.lineBottom} left={item.lineLeft} />
            </React.Fragment>
          ))}
          {horizontalItems.map((item, index) => (
            <React.Fragment key={`horizontal-item-${index}`}>
              <StyledText top={item.textTop} left={item.textLeft} width={item.width} textAlign={item.textAlign}>
                {item.label}
              </StyledText>
              <HorizontalLine top={item.lineTop} width={item.lineWidth} left={item.lineLeft} />
            </React.Fragment>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
